import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Star } from 'lucide-react';
import { cn } from '../lib/utils';
import { useLanguage } from '../contexts/LanguageContext';

const Hero: React.FC = () => {
  const { t } = useLanguage();
  
  return (
    <section className="relative w-full min-h-[700px] lg:min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/4 -left-1/4 w-96 h-96 bg-yellow-300 opacity-10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-white opacity-5 rounded-full blur-3xl"></div>
      </div>

      <div className="container relative z-10 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-[700px] lg:min-h-screen py-16 lg:py-0">
        {/* Left Content */}
        <div className="space-y-8 text-center lg:text-left">
          <div className="space-y-4">
            <h1 className="text-5xl sm:text-6xl xl:text-7xl font-extrabold leading-tight">
              <span className="block text-white drop-shadow-lg">{t('heroTitle1')}</span>
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-yellow-300 to-white drop-shadow-lg">
                {t('heroTitle2')}
              </span>
            </h1>
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-200">
              {t('heroSubtitle')}
            </h2>
            <p className="text-lg sm:text-xl text-blue-200 max-w-lg mx-auto lg:mx-0">
              {t('heroDescription')}
            </p>
          </div>

          {/* Service Tags */}
          <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
            {[t('heroTag1'), t('heroTag2'), t('heroTag3')].map((tag, index) => (
              <span
                key={index}
                className="px-4 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl text-white font-medium shadow-md"
              >
                {tag}
              </span>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
            <Button
              asChild
              size="lg"
              className="bg-yellow-400 hover:bg-yellow-300 text-blue-900 text-lg font-bold shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
            >
              <a href="#contact">
                {t('heroCTA1')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="border-2 border-white/80 bg-transparent text-white hover:bg-white/20 text-lg font-bold shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
            >
              <a href="#services">{t('heroCTA2')}</a>
            </Button>
          </div>
        </div>

        {/* Right Image */}
        <div className="relative w-full min-h-[400px] lg:min-h-full rounded-2xl overflow-hidden shadow-2xl">
          <img
            src="/images/hero-moving.png"
            alt="悉尼家家顺搬家公司团队正在搬运家具"
            className="w-full h-full object-cover object-center"
            loading="eager"
            decoding="async"
          />
          {/* Rating Badge */}
          <div className="absolute right-4 bottom-4 sm:right-8 sm:bottom-8 animate-fade-in-up">
            <div className="flex items-center gap-3 bg-black/50 backdrop-blur-lg rounded-2xl p-4 border border-white/30 shadow-2xl">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <span className="text-white text-base font-bold drop-shadow-md">{t('heroRating')}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
